// src/components/Auth/ForgotPassword.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../SEO/SEOHead';
import {
    Box,
    Card,
    TextField,
    Button,
    Typography,
    Alert,
    InputAdornment,
    CircularProgress,
    Link,
    useTheme,
    Container
} from '@mui/material';
import {
    Email as EmailIcon,
    ArrowBack as ArrowBackIcon,
    Send as SendIcon
} from '@mui/icons-material';

const API_BASE_URL = 'https://soldikeeper-backend-production.up.railway.app/api';

export const ForgotPassword = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [validationError, setValidationError] = useState('');

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setValidationError('');

        if (!email) {
            setValidationError('Email is required');
            return;
        }

        if (!validateEmail(email)) {
            setValidationError('Please enter a valid email address');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email: email.toLowerCase().trim() })
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(true);
                // Show reset URL in development mode
                if (data.resetURL) {
                    console.log('Password Reset URL (DEV MODE):', data.resetURL);
                }
            } else {
                setError(data.message || 'Failed to send reset email');
            }
        } catch (err) {
            console.error('Forgot password error:', err);
            setError('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    if (success) {
        return (
            <Container maxWidth="sm">
                <Box
                    sx={{
                        minHeight: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <Card
                        sx={{
                            p: 4,
                            width: '100%',
                            textAlign: 'center',
                            boxShadow: theme.shadows[10]
                        }}
                    >
                        <Box
                            sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                backgroundColor: theme.palette.success.main,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 20px'
                            }}
                        >
                            <SendIcon sx={{ fontSize: 30, color: 'white' }} />
                        </Box>
                        
                        <Typography variant="h5" gutterBottom fontWeight="600">
                            Check Your Email
                        </Typography>
                        
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Didn't receive an email? Check your spam folder or try again.
                        </Typography>
                        
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={handleBackToLogin}
                            startIcon={<ArrowBackIcon />}
                        >
                            Back to Login
                        </Button>
                    </Card>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm">
            <SEOHead title="Reset Password" noIndex={true} canonical="/forgot-password" description="Reset your SoldiKeeper account password." />
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Card
                    sx={{
                        p: 4,
                        width: '100%',
                        boxShadow: theme.shadows[10]
                    }}
                >
                    <Typography variant="h4" gutterBottom fontWeight="700" textAlign="center">
                        Forgot Password?
                    </Typography>
                    
                    <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
                        Enter your email address and we'll send you a link to reset your password.
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label="Email Address"
                            type="email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                setValidationError('');
                            }}
                            error={!!validationError}
                            helperText={validationError}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ 
                                mb: 3,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2
                                }
                            }}
                        />

                        <Button
                            fullWidth
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                            sx={{
                                py: 1.5,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontSize: '1rem',
                                fontWeight: 600,
                                mb: 2
                            }}
                        >
                            {loading ? 'Sending...' : 'Send Reset Link'}
                        </Button>

                        <Box sx={{ textAlign: 'center' }}>
                            <Link
                                component="button"
                                type="button"
                                variant="body2"
                                onClick={handleBackToLogin}
                                sx={{
                                    textDecoration: 'none',
                                    color: theme.palette.primary.main,
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                ← Back to Login
                            </Link>
                        </Box>
                    </Box>
                </Card>
            </Box>
        </Container>
    );
};

export default ForgotPassword;
